import React from 'react';
import { Alert, CircularProgress, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import {
  AccountData,
  DateAvailabilityOut,
  OrderData,
  OrderPageTypes,
  VoucherOut,
  VoucherType,
} from '../../../../../api';
import { getOrderPages } from '../../../../../utils/AccountUtils';
import { customerFacingPriceFormatter } from '../../../../../utils/utils';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { EditOrderFormValues } from '../../EditOrderTypes';
// @ts-ignore
import { useEditOrder } from '../../EditOrderContext';
import OrderDetails from '../../OrderDetails';
import { EditOrderDialogIntent } from '../../components/footer/EditOrderDialogFooter';
import { ChooseWorkshopSelect } from '../../components/chooseWorkshopSelect/ChooseWorkshopSelect';
import { PrefillButton } from '../../externalPrefill/PrefillButton';
import { DateAndEventTimeFields } from '../../components/dateAndTimePanel/DateAndEventTimeFields';
import { StartTimePicker } from '../components/StartTimePicker';
import { DurationChips } from '../components/DurationChips';
import { AreaSelect } from '../components/AreaSelect';
import { StepOneError } from '../wizardSteps';

const STEP_ONE_ERROR_KEYS: Record<StepOneError, string> = {
  [StepOneError.MISSING_DATE]: 'calendar.edit_order.errors.missingDate',
  [StepOneError.MISSING_TIME]: 'calendar.edit_order.errors.missingTime',
  [StepOneError.END_BEFORE_START]: 'calendar.create_order.endBeforeStart',
};

type WhatWhenStepProps = {
  editOrderDialogIntent: EditOrderDialogIntent;
  cloneSourceOrder?: OrderData;
  voucherToRedeem?: VoucherOut;
  availableDatesForEvents: DateAvailabilityOut[];
  isLoadingDates: boolean;
  stepOneError?: StepOneError;
};

export const WhatWhenStep = ({
  editOrderDialogIntent,
  cloneSourceOrder,
  voucherToRedeem,
  availableDatesForEvents,
  isLoadingDates,
  stepOneError,
}: WhatWhenStepProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const { values, setFieldValue } = useFormikContext<EditOrderFormValues>();
  // @ts-ignore
  const { editOrderState, setOrderPage } = useEditOrder();

  const isEventOrCourse = [OrderPageTypes.EVENT, OrderPageTypes.COURSE].includes(
    editOrderState.orderPage.order_page_type,
  );
  const duration = values.privateOrderEndTimeSeconds - values.timeSeconds;
  const hasNoDates = isEventOrCourse && !isLoadingDates && availableDatesForEvents.length === 0;

  return (
    <Grid container flexDirection="column" gap={1.5}>
      {editOrderDialogIntent === EditOrderDialogIntent.CreateOrder && <PrefillButton />}
      {cloneSourceOrder && (
        <Alert severity="info">
          {t('calendar.create_order.cloneBanner', { number: cloneSourceOrder.order_number })}
        </Alert>
      )}
      {voucherToRedeem?.type === VoucherType.MONETARY && (
        <Alert severity="info">
          {t('vouchers.redeemBalanceBanner', {
            amount: customerFacingPriceFormatter(
              voucherToRedeem.balance ?? 0,
              authState.account as unknown as AccountData,
            ),
          })}
        </Alert>
      )}
      <ChooseWorkshopSelect
        orderPages={getOrderPages(authState.account)}
        editOrderState={editOrderState}
        setOrderPage={setOrderPage}
        setExpandedPanel={() => undefined}
      />
      <OrderDetails errors={{}} isLoadingDates={isLoadingDates} hidePricing />
      {isLoadingDates ? (
        <Grid container justifyContent="center" py={2}>
          <CircularProgress size={24} />
        </Grid>
      ) : hasNoDates ? (
        <Typography variant="body2" color="text.secondary">
          {t('calendar.edit_order.no_available_dates')}
        </Typography>
      ) : (
        <>
          <Grid container item gap={2} flexWrap="nowrap" alignItems="center">
            <DateAndEventTimeFields availableDatesForEvents={availableDatesForEvents} />
          </Grid>
          {cloneSourceOrder && !values.date && (
            <Typography variant="caption" color="text.secondary">
              {t('calendar.create_order.pickNewDate')}
            </Typography>
          )}
          {!isEventOrCourse && (
            <>
              <StartTimePicker
                date={values.date}
                timeSeconds={values.timeSeconds}
                onChange={(seconds) => {
                  // The end moves with the start so the chosen duration is kept.
                  void setFieldValue('privateOrderEndTimeSeconds', seconds + duration);
                  void setFieldValue('timeSeconds', seconds);
                }}
              />
              <DurationChips />
              <AreaSelect />
            </>
          )}
        </>
      )}
      {stepOneError && <Alert severity="error">{t(STEP_ONE_ERROR_KEYS[stepOneError])}</Alert>}
    </Grid>
  );
};
